import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CUST_ANIM } from '../../../theme/customizer-tokens';

/**
 * Cycles a list of status phrases as a live "Lumio is working" line: types each
 * phrase out char-by-char, holds it once complete, erases it, then advances to the
 * next (looping forever). Returns the current visible substring, so the caller can
 * render it under a blinking caret.
 *
 * `active` gates the whole thing — pass `false` (e.g. once deploy is done) to clear
 * it. `prefers-reduced-motion` short-circuits to the first phrase shown in full,
 * with no animation. `phrases` MUST be a stable reference (memoize at the call site,
 * e.g. via {@link useDeployStatusPhrases}); a fresh array each render restarts the
 * type loop from scratch.
 */
export function useTypewriterStatus(phrases: string[], active: boolean): string {
  const [text, setText] = useState('');

  useEffect(() => {
    if (!active || phrases.length === 0) {
      setText('');
      return undefined;
    }

    // Respect reduced-motion: show the first phrase, no typing.
    const reduced =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      setText(phrases[0]);
      return undefined;
    }

    let timer: number | undefined;
    let phraseIdx = 0;
    let charIdx = 0;
    let erasing = false;

    const tick = (): void => {
      const full = phrases[phraseIdx % phrases.length] ?? '';
      if (!erasing) {
        charIdx += 1;
        setText(full.slice(0, charIdx));
        if (charIdx >= full.length) {
          erasing = true; // fully typed → hold, then start erasing
          timer = window.setTimeout(tick, CUST_ANIM.STATUS_HOLD_MS);
        } else {
          timer = window.setTimeout(tick, CUST_ANIM.STATUS_TYPE_CHAR_MS);
        }
      } else {
        charIdx -= 1;
        setText(full.slice(0, Math.max(0, charIdx)));
        if (charIdx <= 0) {
          erasing = false; // erased → pause, then type the next phrase
          phraseIdx += 1;
          timer = window.setTimeout(tick, CUST_ANIM.STATUS_GAP_MS);
        } else {
          timer = window.setTimeout(tick, CUST_ANIM.STATUS_ERASE_CHAR_MS);
        }
      }
    };

    timer = window.setTimeout(tick, CUST_ANIM.STATUS_TYPE_CHAR_MS);
    return () => {
      if (timer != null) window.clearTimeout(timer);
    };
  }, [active, phrases]);

  return text;
}

/**
 * The rotating phrases for the deploy "Lumio is working" status line. First-person,
 * reassuring narration in Lumio's voice — higher-level than the concrete websocket
 * steps listed below it, so the panel reads as actively working even before the
 * first step streams in. Memoized on `t` so a language switch re-translates without
 * restarting the type loop every render.
 */
export function useDeployStatusPhrases(): string[] {
  const { t } = useTranslation();
  // Keep each phrase SHORT so it fits the panel on ONE line (the heading also
  // hard-clips to a single line — see TypingHeading — so a long one never wraps and
  // shifts the layout).
  return useMemo(
    () => [
      t('I’m putting it together…'),
      t('Spinning up your casino…'),
      t('Wiring everything up…'),
      t('Setting the stage…'),
      t('Almost there, hang tight…'),
    ],
    [t],
  );
}
